import {
  FilterSelectorColumnTypes as Types,
  FilterSelectorOperators as Operators,
} from "@etframework/ui.components";
import moment from "moment";

/*
    This module exists because Syncfusion and other external tools don't support all OData functions. 
    Specifically, the 'any' and 'all'
*/

export const buildFilter = (field, operator, value, settings) => {
  if (settings?.OData?.overrideField) {
    field = settings.OData.overrideField;
  }

  if (settings?.OData?.isCollection) {
    return buildArrayFilter(field, operator, value, settings);
  } else {
    return buildSimpleFilter(
      field,
      operator,
      value,
      settings,
      operator === Operators.Equal ? "or" : "and"
    );
  }
};

/*
    This function uses the OData lambda functions 'any' and 'all'
    http://docs.oasis-open.org/odata/odata/v4.01/cs01/part2-url-conventions/odata-v4.01-cs01-part2-url-conventions.html#sec_LambdaOperators
*/
export const buildArrayFilter = (field, operator, value, settings) => {
  let props = field.split(".");
  let param = "p";
  let filters = [];

  if (!Array.isArray(value)) value = [value];

  value?.forEach((x) => {
    var innerFilters = buildSimpleFilter(
      `${param}/${props[1]}`,
      operator,
      [x],
      settings,
      "and"
    );

    if (Array.isArray(settings?.OData?.constants)) {
      settings.OData.constants.forEach((value) => {
        innerFilters +=
          " and " +
          buildSimpleFilter(
            `${param}/${value.field}`,
            value.operator,
            [value.value],
            value.settings,
            "and"
          );
      });
    }

    if (operator === Operators.NotEqual)
      filters.push(`(${props[0]}/all(${param}: ${innerFilters}))`);
    else filters.push(`(${props[0]}/any(${param}: ${innerFilters}))`);
  });

  //if (Array.isArray(settings?.OData?.constants)) {
  //   settings.OData.constants.forEach((value) => {
  //     filters.push(
  //       buildArrayFilter(
  //         value.field,
  //         value.operator,
  //         [value.value],
  //         value.settings
  //       )
  //     );
  //   });
  // }

  return `(${filters.join(" and ")})`;
};

const buildSimpleFilter = (
  field,
  operator,
  value,
  settings,
  predicate = "and"
) => {
  let filters = [];
  if (Array.isArray(value)) {
    value.forEach((v) => {
      filters.push(getFilterString(field, operator, v, settings));
    });
  } else {
    filters.push(getFilterString(field, operator, value, settings));
  }

  // if (Array.isArray(settings?.OData?.constants)) {
  //   settings.OData.constants.forEach((value) => {
  //     filters.push(
  //       getFilterString(
  //         value.field,
  //         value.operator,
  //         value.value,
  //         value.settings
  //       )
  //     );
  //   });
  // }

  return "(" + filters.join(` ${predicate} `) + ")";
};

const getFilterString = (field, operator, value, settings) => {
  if (settings?.OData?.cast) {
    value = cast(value, settings.OData.cast);
  }

  if (settings?.OData?.namespace) {
    value = namespace(value, settings.OData.namespace);
  }

  if (settings?.OData?.or) {
    /* RECURSION */
    let s = { ...settings, OData: { ...settings.OData, or: null } };
    return `(${getFilterString(field, operator, value, s)} or ${getFilterString(
      settings?.OData?.or,
      operator,
      value,
      s
    )})`;
  } else if (settings?.OData?.and) {
    /* RECURSION */
    let s = { ...settings, OData: { ...settings.OData, and: null } };
    return `(${getFilterString(
      field,
      operator,
      value,
      s
    )} and ${getFilterString(settings?.OData?.and, operator, value, s)})`;
  } else if (
    settings?.type === Types.date ||
    settings?.type === Types.dateRange ||
    value instanceof Date
  ) {
    return getDateFilterString(field, operator, value, settings);
  } else if (!settings?.OData?.isLiteral) {
    return `(${field.replace(".", "/")} ${operator} ${value})`;
  } else {
    return `(${field.replace(".", "/")} ${operator} '${value}')`;
  }
};

const cast = (value, type) => `cast(${value},${type})`;
const namespace = (value, namespace) => `${namespace}'${value}'`;

const getDateFilterString = (field, operator, value, settings) => {
  var startOfDate = moment(value).startOf("day").toISOString();
  var endOfDate = moment(value).endOf("day").toISOString();

  if (settings.OData.isLiteral) startOfDate = `'${startOfDate}'`;
  if (settings.OData.isLiteral) endOfDate = `'${endOfDate}'`;

  if (settings?.OData?.exactHour === true) {
    return `(${field.replace(".", "/")} ${operator} ${value.toISOString()})`;
  } else if (operator === "eq") {
    /* 
        turn Date eq 15/08/2025 8:0:0
        to (
          Date gt 15/08/2025 0:0:0
          and
          Date lt 15/08/2025 23:00:00
        )
    */
    let startOfDayFilter = `(${field.replace(".", "/")} ge ${startOfDate})`;
    let endOfDayFilter = `(${field.replace(".", "/")} le ${endOfDate})`;
    return `(${startOfDayFilter} and ${endOfDayFilter})`;
  } else if (operator === "ne") {
    /* 
        turn Date eq 15/08/2025 8:0:0
        to (
          Date lt 15/08/2025 0:0:0
          and
          Date gt 15/08/2025 23:00:00
        )
    */
    let startOfDayFilter = `(${field.replace(".", "/")} lt ${startOfDate})`;
    let endOfDayFilter = `(${field.replace(".", "/")} gt ${endOfDate})`;
    return `(${startOfDayFilter} or ${endOfDayFilter})`;
  } else {
    if (settings?.OData?.time) {
      var time = moment(settings?.OData?.time, "HH:mm:ss");
      value = moment(value)
        .set({
          hour: time.hours(),
          minute: time.minutes(),
          seconds: time.seconds(),
        })
        .toDate()
        .toISOString();
    } else if (operator === "gt" || operator === "le") {
      value = moment(value).endOf("day").toDate().toISOString();
    } else if (operator === "ge" || operator === "lt") {
      value = moment(value).startOf("day").toDate().toISOString();
    }

    if (settings.OData.isLiteral) value = `'${value}'`;

    return `(${field.replace(".", "/")} ${operator} ${value})`;
  }
};
